import { ConflictType, EXPORT_COLORS } from '@consigli/types';
import { MIME_TYPES, saveBlob } from '@consigli/utils';
import ExcelJS from 'exceljs';
import { TFunction } from 'i18next';

import { fetchLogoAsArrayBuffer } from '@/pages/meeting-secretary/utils/logo-utils';

import type { BidComparisonCell, BidComparisonData } from './bid-comparison-types';

// Light cell fill + text color mirroring the on-screen tone marks (green for a
// fully-addressed item, amber for a partial one or a deviation) so the exported
// sheet keeps the same at-a-glance signal as the table.
const TONE_STYLE: Record<string, { fill: string; font: string }> = {
  [ConflictType.ADDRESSED]: { fill: 'FFF0FDF4', font: 'FF14532D' },
  [ConflictType.PARTIAL]: { fill: 'FFFFFBEB', font: 'FF78350F' },
  [ConflictType.DEVIATION]: { fill: 'FFFFFBEB', font: 'FF78350F' },
};

const thinBorder: Partial<ExcelJS.Borders> = {
  top: { style: 'thin', color: { argb: 'FFB0B0B0' } },
  left: { style: 'thin', color: { argb: 'FFB0B0B0' } },
  bottom: { style: 'thin', color: { argb: 'FFB0B0B0' } },
  right: { style: 'thin', color: { argb: 'FFB0B0B0' } },
};

const applyTone = (target: ExcelJS.Cell, cell: BidComparisonCell | undefined): void => {
  const tone = cell?.conflictType ? TONE_STYLE[cell.conflictType] : undefined;
  if (!tone) {
    return;
  }
  target.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tone.fill } };
  target.font = { size: 11, color: { argb: tone.font } };
};

export interface BidComparisonExportParams {
  data: BidComparisonData;
  /** Live, user-edited leveling weights keyed by row id. */
  weights: Record<string, number>;
  packageName?: string;
  t: TFunction;
}

export const exportBidComparisonToExcel = async ({
  data,
  weights,
  packageName,
  t,
}: BidComparisonExportParams): Promise<void> => {
  const { contractors, rows } = data;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Consigli';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(t('bid-comparison.title', 'Bid Comparison'));

  const headers = [
    t('bid-comparison.col-item', 'Item'),
    t('bid-comparison.col-category', 'Category'),
    t('bid-comparison.col-weight', 'Weight'),
    ...contractors.map((contractor) => contractor.name),
  ];

  // Row 1: AECOM branded title row, matching the backend's standard Excel export.
  const titleRow = worksheet.getRow(1);
  const titleText = `AI For Eng – ${t('bid-comparison.title', 'Bid Comparison')}`;
  const lastColumnIndex = headers.length;
  const titleCell = titleRow.getCell(lastColumnIndex);
  titleCell.value = titleText;
  titleCell.font = { size: 20 };
  titleCell.alignment = { horizontal: 'right' };
  titleRow.height = 40;

  try {
    const logoBuffer = await fetchLogoAsArrayBuffer();
    const logoImageId = workbook.addImage({ buffer: logoBuffer, extension: 'png' });
    worksheet.addImage(logoImageId, {
      tl: { col: 0, row: 0 },
      ext: { width: 270, height: 50 },
    });
  } catch {
    // Logo fetch can fail in tests or offline; the export still works without it.
  }

  // White fill on the title row so it looks clean.
  for (let column = 1; column <= lastColumnIndex; column++) {
    const cell = titleRow.getCell(column);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXPORT_COLORS.white.argb } };
    cell.border = { bottom: { style: 'thin', color: { argb: 'FFB0B0B0' } } };
  }

  // Row 2: column headers.
  const headerRow = worksheet.getRow(2);
  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: EXPORT_COLORS.headerGrey.argb },
    };
    cell.font = { bold: true, size: 11, color: { argb: EXPORT_COLORS.black.argb } };
    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
    cell.border = thinBorder;
  });
  headerRow.height = 20;

  // Row 3+: data rows.
  rows.forEach((row, rowIndex) => {
    const worksheetRow = worksheet.getRow(rowIndex + 3);

    const itemCell = worksheetRow.getCell(1);
    itemCell.value = row.item;
    itemCell.font = { size: 11, bold: true };

    worksheetRow.getCell(2).value = row.category;

    const weightCell = worksheetRow.getCell(3);
    weightCell.value = weights[row.id] ?? row.weight;
    weightCell.numFmt = '0"%"';
    weightCell.alignment = { vertical: 'top', horizontal: 'right' };

    contractors.forEach((contractor, contractorIndex) => {
      const match = row.cells.find((cell) => cell.contractorId === contractor.id);
      const target = worksheetRow.getCell(4 + contractorIndex);
      target.value = match?.value ?? '—';
      applyTone(target, match);
    });

    worksheetRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.alignment = { ...cell.alignment, vertical: 'top', wrapText: true };
      cell.border = thinBorder;
    });
  });

  const totalWeight = rows.reduce((total, row) => total + (weights[row.id] ?? row.weight ?? 0), 0);
  const totalRow = worksheet.getRow(rows.length + 3);
  const totalLabelCell = totalRow.getCell(1);
  totalLabelCell.value = t('bid-comparison.total-weighting', 'Total weighting');
  totalLabelCell.font = { bold: true, size: 11 };
  const totalValueCell = totalRow.getCell(3);
  totalValueCell.value = totalWeight;
  totalValueCell.numFmt = '0"%"';
  totalValueCell.font = { bold: true, size: 11 };
  totalValueCell.alignment = { horizontal: 'right' };

  worksheet.getColumn(1).width = 38;
  worksheet.getColumn(2).width = 20;
  worksheet.getColumn(3).width = 12;
  contractors.forEach((_, contractorIndex) => {
    worksheet.getColumn(4 + contractorIndex).width = 42;
  });

  // Keep the header and the leftmost identifying columns in view while scrolling
  // the (potentially many) contractor columns, matching the sticky table.
  worksheet.views = [{ state: 'frozen', xSplit: 3, ySplit: 2 }];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: MIME_TYPES.xlsx });
  const namePrefix = packageName?.trim() ? `${packageName.trim()}_` : '';
  saveBlob(`${namePrefix}bid-comparison.xlsx`, blob);
};
